import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ShieldCheck, AlertCircle, Loader2, Lock, Mail, Eye, EyeOff } from 'lucide-react';
import { adminApi, isAuthenticated } from '../../services/adminApi';
import { loadGoogleIdentity, useGoogleIdentity } from '../../hooks/useGoogleIdentity';
import useNoIndex from '../../hooks/useNoIndex';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const AdminLogin = () => {
  useNoIndex();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const { ready, error: loadError } = useGoogleIdentity();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  // Keep the latest navigate in a ref so the GIS callback never goes stale.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!ready || !GOOGLE_CLIENT_ID || !buttonRef.current) return;

    let cancelled = false;
    loadGoogleIdentity().then((gis) => {
      if (cancelled) return;
      gis.initialize({
        client_id: GOOGLE_CLIENT_ID,
        ux_mode: 'popup',
        auto_select: false,
        cancel_on_tap_outside: true,
        callback: async ({ credential }) => {
          setError('');
          setSigningIn(true);
          try {
            await adminApi.loginWithGoogle(credential);
            navigateRef.current('/admin/dashboard', { replace: true });
          } catch (err) {
            if (err.status === 403) {
              setError(
                'This Google account is not authorized to access the dashboard. ' +
                  'Please sign in with an approved account.'
              );
            } else {
              setError(err.message || 'Sign-in failed. Please try again.');
            }
            setSigningIn(false);
          }
        },
      });
      gis.renderButton(buttonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'signin_with',
        logo_alignment: 'left',
        width: 300,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [ready]);

  if (isAuthenticated()) return <Navigate to="/admin/dashboard" replace />;

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSigningIn(true);
    try {
      await adminApi.loginWithPassword(username, password);
      navigate('/admin/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Invalid username or password.');
      setSigningIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-sky-50 flex items-center justify-center p-4">
      <div className="bg-white/90 backdrop-blur rounded-3xl shadow-xl border border-gray-100 w-full max-w-md p-8 sm:p-10">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TODOenBALANCE" className="h-14 mx-auto mb-5" />
          <h1 className="text-2xl font-bold text-gray-900">Admin Portal</h1>
          <p className="text-gray-500 text-sm mt-1">TODOenBALANCE · Paola Michelle</p>
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="admin-username"
              className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5"
            >
              Username / Email
            </label>
            <div className="relative">
              <Mail className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="admin-username"
                type="email"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="paola@todoenbalance.com"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm text-gray-900 transition-all"
              />
            </div>
          </div>

          <div>
            <label
              htmlFor="admin-password"
              className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5"
            >
              Password
            </label>
            <div className="relative">
              <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full pl-10 pr-10 py-2.5 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm text-gray-900 transition-all"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={signingIn}
            className="w-full bg-primary hover:bg-blue-600 disabled:opacity-60 text-white font-semibold py-2.5 px-4 rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2 text-sm"
          >
            {signingIn ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Signing you in…
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {GOOGLE_CLIENT_ID && !loadError && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-3 text-gray-400">or continue with</span>
              </div>
            </div>

            <div className="flex flex-col items-center gap-3">
              {!ready && !signingIn && (
                <div className="flex items-center gap-2 text-gray-400 py-2 text-xs">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading Google sign-in…
                </div>
              )}
              <div ref={buttonRef} className={`min-h-[44px] ${signingIn ? 'hidden' : ''}`} />
            </div>
          </>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
          <ShieldCheck className="w-4 h-4" />
          Access restricted to authorized administrators
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
